// Sales · Trials — the trial registry & conversion funnel, derived from the MRR
// rows (is_trial + trial_status/trial_days_left). Convert → Quote Builder.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { PageHeader, Card, CardHead, Table, Td, Row, Btn } from '@/components/shared/primitives';
import { Kpi } from '@/components/shared/Kpi';
import { Pill } from '@/components/shared/badges';
import { EmptyState } from '@/components/shared/EmptyState';
import { AlertBanner } from '@/components/shared/AlertBanner';
import { PERSONA } from '@/lib/persona';
import { getMrrAnalytics, type MrrAnalytics, type MrrRow } from '@/lib/api/sales';

const SALES = PERSONA.sales.accent;

export function SalesTrials() {
  const navigate = useNavigate();
  const [data, setData] = useState<MrrAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getMrrAnalytics().then(setData).catch((e) => setError(e instanceof Error ? e.message : 'Failed to load trials')).finally(() => setLoading(false));
  }, []);

  const trials = useMemo(() => (data?.rows || []).filter((r) => r.is_trial), [data]);
  const active = trials.filter((t) => t.trial_status === 'active' || (t.status === 'active' && t.trial_status !== 'converted' && t.trial_status !== 'expired'));
  const expiring = active.filter((t) => t.trial_days_left != null && (t.trial_days_left as number) <= 7);
  const convRate = data ? Math.round(data.conversion_rate * 100) : 0;

  return (
    <div>
      <PageHeader title="Trials" subtitle={`${active.length} active · ${convRate}% all-time conversion`}
        actions={<Btn kind="primary" accent={SALES} icon="gift" onClick={() => navigate('/sales/console')}>Issue Trial</Btn>} />
      {error && <div style={{ marginBottom: 16 }}><AlertBanner kind="error">{error}</AlertBanner></div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 22 }}>
        <Kpi label="Active Trials" value={active.length} accent={SALES} icon="gift" />
        <Kpi label="Conversion Rate" value={convRate} unit="%" accent={SALES} icon="trending-up" />
        <Kpi label="Expiring ≤7d" value={expiring.length} deltaKind={expiring.length ? 'warn' : 'up'} delta={expiring.length ? 'needs nudge' : undefined} accent={SALES} icon="clock" />
      </div>
      <Card style={{ overflow: 'hidden' }}>
        <CardHead title="Active trials" sub="Nudge before expiry; convert the engaged ones" />
        {loading ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div> : active.length === 0 ? (
          <EmptyState icon="gift" title="No active trials" body="Issue a trial from the Account Console to start a conversion funnel." action={() => navigate('/sales/console')} actionLabel="Issue Trial" />
        ) : (
          <Table cols={[{ label: 'Company' }, { label: 'Customer type' }, { label: 'Status' }, { label: 'Expires' }, { label: '', align: 'right' }]}>
            {active.map((t: MrrRow) => (
              <Row key={t.license_id} onClick={() => navigate(`/sales/licenses/${t.license_id}`)}>
                <Td style={{ fontWeight: 600 }}>{t.customer_name || '—'}</Td>
                <Td style={{ color: 'var(--text-secondary)', fontSize: 12.5 }}>{t.customer_type || 'Trial'}</Td>
                <Td><Pill bg="var(--ps-pill-purple-bg)" fg="var(--ps-pill-purple-tx)">{t.trial_status || 'active'}</Pill></Td>
                <Td><span style={{ color: (t.trial_days_left ?? 99) <= 7 ? 'var(--status-expired)' : 'var(--text-secondary)', fontWeight: (t.trial_days_left ?? 99) <= 7 ? 600 : 400 }}>{t.trial_days_left != null ? `${t.trial_days_left}d left` : '—'}</span></Td>
                <Td align="right" onClick={(e) => e.stopPropagation()}><Btn sm kind="primary" accent={SALES} icon="arrow-up-circle" onClick={() => navigate('/sales/quotes/new')}>Convert</Btn></Td>
              </Row>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}
export default SalesTrials;
